import axios from 'axios';
import qs from 'qs';

/**
 * null/undefinedかどうかを判定する
 * @param value チェック対象
 * @returns null/undefinedの場合true
 */
const isNullish = (value: any) => value === undefined || value === null;

/**
 * 空白文字のみチェック
 * @param value チェック対象文字列
 * @returns 空白文字のみであればtrue, 空白文字以外が入っていればfalse
 */
export function isEmpty(value: string): boolean {
  if (typeof value !== 'string') return false;
  return value.match(/[\S]/) === null;
}

/**
 * 機種依存文字が含まれているかチェック
 * @param value チェック対象文字列
 * @returns 機種依存文字が含まれている場合true, 機種依存文字が含まれていない場合false
 */
export function isDependentCharacter(value: string): boolean {
  if (isNullish(value)) return false;
  const searchTxt =
    '[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩ㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡㍻〝〟№㏍℡㊤㊥㊦㊧㊨㈱㈲㈹㍾㍽㍼]';
  return Boolean(value.match(searchTxt));
}

/**
 * メールアドレス形式になっているかチェック
 * @param value チェック対象文字列
 * @returns メールアドレス形式になっていればtrue, メールアドレス形式になっていないならfalse
 */
export function isEmailFormat(value: string): Boolean {
  if (isNullish(value)) return false;

  // @の前後で分割
  const mail = value.split('@');

  // @が1つであることを確認
  if (mail.length !== 2) return false;

  // @の前
  // サーバサイド: Pattern.compile("[!#$%&'*+\\-./=?^_`{|}~\\[\\]0-9a-zA-Z]+");
  // その後記号を『-._!#$%&'*+/=?^`{|}~』に絞り込み
  const accountResult = mail[0].match(/^[!#$%&'`*+\-./=?^_{|}~0-9a-zA-Z]+$/);
  if (accountResult === null) return false;

  // @の後ろ
  // サーバサイド: Pattern.compile("[a-z0-9-_]+(\\.[a-z0-9-_]+)+");
  const domainResult = mail[1].match(/^[a-z0-9-_]+(\.[a-z0-9-_]+)+$/);
  if (domainResult === null) return false;

  return true;
}

/**
 * Quipperメールアドレス形式になっているかチェック
 *   - 3-161文字チェック
 *   - 半角英数記号文字チェック（空白除く）（!"#$%&'()*+-./:;<=>?@[\]^_`{|}~0-9a-zA-Z）
 *   - '@'が一つかチェック
 * @param value チェック対象文字列
 * @returns Quipperメールアドレス形式になっていればtrue, メールアドレス形式になっていないならfalse
 */
export function isQuipperEmailFormat(value: string): Boolean {
  if (isNullish(value)) return false;
  // 文字数チェック
  if (value.length < 3 && value.length > 161) return false;

  // @が1つであることを確認
  if (value.split('@').length !== 2) return false;

  // 正規表現チェック
  const checked = value.match(/^[!#$%&'`*+ -./=?^_{|}~0-9a-zA-Z"():;<>@[\\\]]+$/);
  if (checked === null) return false;

  return true;
}

/**
 * 全角が含まれているかチェック
 * @param value チェック対象文字列
 * @returns 全角が含まれている場合にtrue, すべて半角のときfalse
 */
export function isFullWidth(value: string): boolean {
  if (isNullish(value)) return false;
  // eslint-disable-next-line no-control-regex
  return Boolean(value.match(/[^\x01-\x7E\xA1-\xDF]+/));
}

/**
 * 半角カナが含まれているかチェック
 * @param value チェック対象文字列
 * @returns 半角カナが含まれていたらtrue, そうでない場合はfalse
 */
export function isHalfWidthKana(value: string): boolean {
  if (isNullish(value)) return false;
  return Boolean(value.match(/[ｦ-ﾟ]/));
}

/**
 * すべてひらがなチェック
 * @param value チェック対象文字列
 * @returns すべてひらがなである場合にtrue, ひらがな以外が混ざっている場合はfalse
 */
export function isAllHiragana(value: string): boolean {
  if (isNullish(value)) return false;
  // eslint-disable-next-line no-control-regex
  return Boolean(value.match(/^[ぁ-ん\u2014\u2015\u30fc]+$/));
}

/**
 * すべてカタカナチェック
 * @param value チェック対象文字列
 * @returns すべてカタカナである場合にtrue, カタカナ以外が混ざっている場合はfalse
 */
export function isAllKatakana(value: string): boolean {
  if (isNullish(value)) return false;
  // eslint-disable-next-line no-control-regex
  return Boolean(value.match(/^[ァ-ヶー\u30A0-\u30FF]+$/));
}

/**
 * すべて数値チェック
 * @param value チェック対象文字列
 * @returns すべて数字である場合はtrue, 数字以外が混ざっている場合はfalse
 */
export function isAllNumber(value: string): boolean {
  if (isNullish(value)) return false;
  return Boolean(value.match(/^[0-9]+$/));
}

/**
 * 文字数チェック
 * @param value チェック対象文字列
 * @param min 最小文字数
 * @param max 最大文字数
 * @returns チェック範囲内ならtrue, チェック範囲外ならfalse
 */
export function checkWordCount(value: string, min: number, max: number): Boolean {
  if (isNullish(value)) return false;
  return value.length >= min && value.length <= max;
}

/**
 * 数字含むかチェック
 * @param value チェック対象文字列
 * @returns 数字を１つでも含んでいれば一致文字列
 */
export function isIncludeNumber(value: string): boolean {
  if (isNullish(value)) return false;
  return Boolean(value.match(/[0-9]/g));
}

/**
 * 英字含むかチェック
 * @param value チェック対象文字列
 * @returns 英字を１つでも含んでいればtrue
 */
export function isIncludeEnglish(value: string): boolean {
  if (isNullish(value)) return false;
  return Boolean(value.match(/[a-zA-Z]/g));
}

/**
 * ユーザ名チェック
 * @param value チェック対象文字列
 * @returns ユーザ名のフォーマットにあっていればtrue, あっていなければfalse
 */
export function isUserNameFormat(value: string): boolean {
  if (isNullish(value)) return false;
  return Boolean(value.match(/^[0-9a-z-_.]+$/));
}

/**
 * S JISチェック
 */
export async function checkCharCode(value: string, loadingFlag: boolean | null): Promise<number> {
  // 初期表示で実行されるバリデーションは非同期処理直列化の処理を通す
  if (loadingFlag) {
    return new Promise((resolve) => {
      const changeButtonInterval = window.setInterval(() => {
        // 非同期処理直列化 ajaxFlagはグローバル変数で、非同期処理実行判定フラグとして用いている
        if (!(window as any).ajaxFlag) return;
        (window as any).ajaxFlag = false;

        axios
          .post('/net2/quipMember/inputCheck/charCodeCheckJson/', qs.stringify({ input: value }))
          .then((response) => {
            if (!response) throw Error;
            if (!response.data) throw Error;
            if (response.data.resultCode === 1) {
              resolve(1);
            } else {
              resolve(0);
            }
          })
          .catch(() => {
            // 無言でOKで確認済
            resolve(0);
          })
          .finally(() => {
            (window as any).ajaxFlag = true;
          });
        // 非同期処理直列化 ajax実行完了したら繰り返し処理を解除する
        window.clearInterval(changeButtonInterval);
      }, 500);
    });
  }
  // 初期表示以外でバリデーションを実行するときは、従来通りの処理を行う
  return new Promise((resolve) => {
    axios
      .post('/net2/quipMember/inputCheck/charCodeCheckJson/', qs.stringify({ input: value }))
      .then((response) => {
        if (!response) throw Error;
        if (!response.data) throw Error;
        if (response.data.resultCode === 1) {
          resolve(1);
        } else {
          resolve(0);
        }
      })
      .catch(() => {
        // 無言でOKで確認済
        resolve(0);
      });
  });
}

/**
 * 同行者文字コードチェック
 */
export async function checkCompanionCharCode(value: string, loadingFlag: boolean | null): Promise<number> {
  // 初期表示で実行されるバリデーションは非同期処理直列化の処理を通す
  if (loadingFlag) {
    return new Promise((resolve) => {
      const changeButtondoukouInterval = window.setInterval(() => {
        // 非同期処理直列化 ajaxFlagはグローバル変数で、非同期処理実行判定フラグとして用いている
        if (!(window as any).ajaxFlag) return;
        (window as any).ajaxFlag = false;
        axios
          .post('/net2/quipMember/inputCheck/companionCharCheckJson/', qs.stringify({ companion: value }))
          .then((response) => {
            if (!response?.data) throw Error;
            return response.data.resultCode === 1 ? resolve(1) : resolve(0);
          })
          .catch(() => {
            // 無言でOKで確認済
            resolve(0);
          })
          .finally(() => {
            (window as any).ajaxFlag = true;
          });
        // 非同期処理直列化 ajax実行完了したら繰り返し処理を解除する
        window.clearInterval(changeButtondoukouInterval);
      }, 500);
    });
  }
  // 初期表示以外でバリデーションを実行するときは、従来通りの処理を行う
  return new Promise((resolve) => {
    axios
      .post('/net2/quipMember/inputCheck/companionCharCheckJson/', qs.stringify({ companion: value }))
      .then((response) => {
        if (!response?.data) throw Error;
        return response.data.resultCode === 1 ? resolve(1) : resolve(0);
      })
      .catch(() => {
        // 無言でOKで確認済
        resolve(0);
      });
  });
}

/**
 * 自由質問欄・学校からの質問 DB登録不可文字コードチェック
 * @param {string} inputValue 入力欄の実際の値
 * @param {boolean | null} awaitLoadingFlag 非同期処理の直列化をさせるか(初期表示時の読み込みなど)
 * @return {Promise<number>} DB登録不可エラー文字の存在有無。存在する: `1`, 存在しない: `0`。
 */
export async function checkInputFreeQuestionAnswerCharCode(
  inputValue: string,
  awaitLoadingFlag: boolean | null,
): Promise<number> {
  const URL = '/net2/quipMember/inputCheck/freeQuestionCheckJson/';
  const HAS_ERROR_CHAR = 1;
  const NOTHING = 0;

  type PostFreeQuestionValidateApi = (resolve: (resultCode: 0 | 1) => void) => Promise<void>;
  const postFreeQuestionValidateApi: PostFreeQuestionValidateApi = (resolve) =>
    axios
      .post(URL, qs.stringify({ freeQuestionAnswer: inputValue }))
      .then((response) => {
        if (!response?.data) {
          throw new Error('no data API response freeQuestion');
        }
        return response.data.resultCode === HAS_ERROR_CHAR ? resolve(HAS_ERROR_CHAR) : resolve(NOTHING);
      })
      .catch(() => resolve(NOTHING));

  // 非同期処理を直列化させる場合の処理(初期表示時など)
  if (awaitLoadingFlag) {
    return new Promise((resolve) => {
      const changeButtonInterval = window.setInterval(() => {
        // グローバル変数`ajaxFlag` を使用した非同期処理直列化
        if (!(window as any).ajaxFlag) return;
        (window as any).ajaxFlag = false;

        postFreeQuestionValidateApi(resolve).finally(() => {
          (window as any).ajaxFlag = true;
        });
        // ajax実行完了で繰り返し処理を解除
        window.clearInterval(changeButtonInterval);
      }, 500);
    });
  }

  return new Promise((resolve) => {
    postFreeQuestionValidateApi(resolve);
  });
}

/**
 * 住所専用文字チェック
 */
export async function checkAddressChar(value: string, loadingFlag: boolean | null): Promise<number> {
  // 初期表示で実行されるバリデーションは非同期処理直列化の処理を通す
  if (loadingFlag) {
    return new Promise((resolve) => {
      const changeButtonzyushoInterval = window.setInterval(() => {
        // 非同期処理直列化 ajaxFlagはグローバル変数で、非同期処理実行判定フラグとして用いている
        if (!(window as any).ajaxFlag) return;
        (window as any).ajaxFlag = false;
        axios
          .post('/net2/quipMember/inputCheck/addressCharCheckJson/', qs.stringify({ address: value }))
          .then((response) => {
            if (!response) throw Error;
            if (!response.data) throw Error;
            if (response.data.resultCode === 1) {
              resolve(1);
            } else {
              resolve(0);
            }
          })
          .catch(() => {
            // 無言でOKで確認済
            resolve(0);
          })
          .finally(() => {
            (window as any).ajaxFlag = true;
          });
        // 非同期処理直列化 ajax実行完了したら繰り返し処理を解除する
        window.clearInterval(changeButtonzyushoInterval);
      }, 500);
    });
  }
  // 初期表示以外でバリデーションを実行するときは、従来通りの処理を行う
  return new Promise((resolve) => {
    axios
      .post('/net2/quipMember/inputCheck/addressCharCheckJson/', qs.stringify({ address: value }))
      .then((response) => {
        if (!response) throw Error;
        if (!response.data) throw Error;
        if (response.data.resultCode === 1) {
          resolve(1);
        } else {
          resolve(0);
        }
      })
      .catch(() => {
        // 無言でOKで確認済
        resolve(0);
      });
  });
}

/**
 * ユーザ名重複チェック
 */
export async function checkUserId(value: string, isSp: boolean): Promise<number> {
  return new Promise((resolve) => {
    axios
      .post('/net2/quipMember/inputCheck/userIdCheckJson/', qs.stringify({ input: value }))
      .then((response) => {
        if (!response) throw Error;
        if (!response.data) throw Error;
        if (response.data.resultCode === 1) {
          resolve(1);
        } else if (response.data.resultCode === 2) {
          const urlPrefix = isSp ? 'smp2' : 'net2';
          // quipper閉塞エラー
          window.location.href = `/${urlPrefix}/error/shinroAccountApiMainte/`;
        } else {
          resolve(0);
        }
      })
      .catch(() => {
        // 無言でOKで確認済
        resolve(0);
      });
  });
}

/**
 * 空白チェック
 * 1個でも空白あったらtrue
 */
export function emptyCheck(inputs: any): boolean {
  let emptyFlg = false;
  Object.keys(inputs).forEach((keyName) => {
    if (typeof inputs[keyName] === 'object') {
      // objectの場合は中身までforEachする
      Object.keys(inputs[keyName]).forEach((insideKeyName) => {
        if (isEmpty(inputs[keyName][insideKeyName])) {
          if (!emptyFlg) {
            emptyFlg = true;
          }
        }
      });
    } else if (isEmpty(inputs[keyName])) {
      if (!emptyFlg) {
        emptyFlg = true;
      }
    }
  });
  return emptyFlg;
}

/**
 * hasInnerErrorチェック
 * 1つでもエラーあればtrue
 */
export function hasInnerErrorCheck(inputs: any, hasInnerError: any): boolean {
  let errorFlg = false;
  Object.keys(inputs).forEach((keyName) => {
    if (typeof inputs[keyName] === 'object') {
      // objectの場合は中身までforEachする
      Object.keys(inputs[keyName]).forEach((insideKeyName) => {
        if (hasInnerError[keyName][insideKeyName]) {
          // 内部でエラー出てるとき(object)
          if (!errorFlg) {
            errorFlg = true;
          }
        }
      });
    } else if (hasInnerError[keyName]) {
      // 内部エラー(not object)
      if (!errorFlg) {
        errorFlg = true;
      }
    }
  });
  return errorFlg;
}

/**
 * @description メールアドレスのオプトアウトチェック
 * @param {string} mailAddress チェック対象のメールアドレス
 * @returns 0: オプトアウトエラーなし, 1: オプトアウトエラーあり
 */
export function checkOptoutError(mailAddress: string): Promise<object> {
  const param = {
    mailAddress,
  };

  return new Promise((resolve, reject) => {
    axios
      .post(`/net2/profileItem/optoutCheckJson/`, qs.stringify(param))
      .then((response) => {
        if (!response) throw Error;
        if (!response.data) throw Error;
        if (response.data.resultCode === 0 || response.data.resultCode === 1) {
          resolve(response.data);
        } else {
          throw Error;
        }
      })
      .catch((error) => {
        // フロント側でシステムエラー画面へ遷移
        reject(error);
      });
  });
}

/**
 * @description 性別表現用コードチェック
 * @param {string} genderExpression チェック対象の性別表現コメント
 * @returns 0: OK, 1: NG
 */
export function checkGenderCharCode(genderExpression: string): Promise<object> {
  const param = {
    genderExpression,
  };

  return new Promise((resolve, reject) => {
    axios
      .post(`/net2/quipMember/inputCheck/genderExpressionCharCodeCheckJson/`, qs.stringify(param))
      .then((response) => {
        if (!response) throw Error;
        if (!response.data) throw Error;
        if (response.data.resultCode === 0 || response.data.resultCode === 1) {
          resolve(response.data);
        } else {
          throw new Error();
        }
      })
      .catch(() => {
        reject(new Error());
        // 無言でOKで確認済
      });
  });
}
